<script lang="ts">
  // Matrix mode — Public Surface view per INTERFACE-SPECIFICATION.md §4
  // and SPEC-CHANGE-CANDIDATES item 17 (Marketing Surface as the full
  // public projection).
  //
  // Two sub-tables under the Public Surface heading:
  //
  //   1. Per public band (rows = Excellent / Good / Fair, columns
  //      per product = from rate, comparison rate, est-fee at band,
  //      max amount). The aggregations a customer reads from the
  //      rate card on the public site.
  //
  //   2. Per product (rows = the product-scoped public-surface fields
  //      that are not band-keyed: available terms, loan amount range,
  //      loan purposes, the full establishment-fee schedule, the flat
  //      fees, the included-in-comparison-rate set). The product
  //      constants a customer sees on the application form, T&Cs, and
  //      calculator.
  //
  // Numbers follow §12.6 — units in the header, decimal-aligned,
  // tabular numerals, em dash for absent values.

  import { PRODUCTS } from '../lib/products';
  import { cachedCatalogue } from '../lib/catalogue-store.svelte';
  import { projectMarketingBand, projectMarketingPerProduct, PUBLIC_BANDS, type ProjectedBand, type MarketingPerProduct } from '../lib/surface-projector';
  import { formatRate, formatAmount } from '../lib/formatters';

  const bands = $derived(buildBandTable());
  const perProduct = $derived(buildPerProductTable());

  function emptyProjection(band: typeof PUBLIC_BANDS[number]): ProjectedBand {
    return {
      public_band: band,
      surface: 'marketing',
      min_rate: null,
      typical_rate: null,
      max_amount: null,
      rate_basis: 'unknown',
      channel: 'hLoan',
      cell_count: 0,
      contributing_credit_rating_ids: [],
      comparison_rate: null,
      establishment_fee: null,
      establishment_fee_source: null
    };
  }

  function buildBandTable(): Record<string, ProjectedBand[]> {
    const rows: Record<string, ProjectedBand[]> = {};
    for (const band of PUBLIC_BANDS) {
      rows[band] = PRODUCTS.map((p) => {
        const cube = cachedCatalogue(p.jurisdiction, p.product);
        if (!cube) return emptyProjection(band);
        return projectMarketingBand(cube, band);
      });
    }
    return rows;
  }

  function buildPerProductTable(): (MarketingPerProduct | null)[] {
    return PRODUCTS.map((p) => {
      const cube = cachedCatalogue(p.jurisdiction, p.product);
      if (!cube) return null;
      return projectMarketingPerProduct(cube);
    });
  }

  const bandLabel: Record<string, string> = {
    excellent: 'Excellent',
    good: 'Good',
    fair: 'Fair'
  };

  function formatAmountRange(r: { min: number; max: number } | null | undefined): string {
    if (!r) return '—';
    return `$${formatAmount(r.min)} – $${formatAmount(r.max)}`;
  }

  function formatEstFeeTiers(tiers: Array<{ min_amount: number; max_amount: number; fee: number }>): string {
    if (tiers.length === 0) return '—';
    return tiers
      .map((t) => `$${formatAmount(t.min_amount)}–$${formatAmount(t.max_amount)}: $${formatAmount(t.fee)}`)
      .join(' · ');
  }
</script>

<section>
  <h3 class="section-heading">Public Surface — by public band</h3>
  <p class="muted">
    Customer-facing aggregations on hLoan. Comparison rate and establishment fee
    computed at the methodology's reference loan amount + term. The per-band
    establishment fee respects the per-credit-rating override when a
    consistent one applies (e.g. AU unsec Excellent: $0 from the
    <code>950_plus</code> override).
  </p>
  <div class="table-scroll">
    <table class="cube-table cube-table--matrix">
      <thead>
        <tr>
          <th rowspan="2">Public band</th>
          {#each PRODUCTS as p (p.jurisdiction + p.product)}
            <th colspan="4" class="header-group">{p.label}</th>
          {/each}
        </tr>
        <tr>
          {#each PRODUCTS as p (p.jurisdiction + p.product)}
            <th class="num">from %</th>
            <th class="num">comparison %</th>
            <th class="num">est fee $</th>
            <th class="num">max $</th>
          {/each}
        </tr>
      </thead>
      <tbody>
        {#each PUBLIC_BANDS as band (band)}
          <tr>
            <td>{bandLabel[band]}</td>
            {#each bands[band] ?? [] as cell, i (PRODUCTS[i]?.jurisdiction + '/' + PRODUCTS[i]?.product)}
              <td class="num">{formatRate(cell.min_rate)}</td>
              <td class="num">{formatRate(cell.comparison_rate)}</td>
              <td class="num">{cell.establishment_fee === null ? '—' : '$' + formatAmount(cell.establishment_fee)}</td>
              <td class="num">{formatAmount(cell.max_amount)}</td>
            {/each}
          </tr>
        {/each}
      </tbody>
    </table>
  </div>

  <h3 class="section-heading">Public Surface — by product</h3>
  <p class="muted">
    Product-scoped public fields. Customer-facing constants that govern the
    application form, T&amp;Cs, and the public calculator. Changes here move
    the public surface even when no rate-cube cell is touched.
  </p>
  <div class="table-scroll">
    <table class="cube-table cube-table--per-product">
      <thead>
        <tr>
          <th>Field</th>
          {#each PRODUCTS as p (p.jurisdiction + p.product)}
            <th>{p.label}</th>
          {/each}
        </tr>
      </thead>
      <tbody>
        <tr>
          <td><strong>Available terms (months)</strong></td>
          {#each perProduct as pp (pp?.jurisdiction + '/' + pp?.product)}
            <td>{pp ? pp.terms_months.join(', ') : '—'}</td>
          {/each}
        </tr>
        <tr>
          <td><strong>Loan amount range</strong></td>
          {#each perProduct as pp (pp?.jurisdiction + '/' + pp?.product)}
            <td>{pp && pp.loan_amount_range ? `$${formatAmount(pp.loan_amount_range.min)} – $${formatAmount(pp.loan_amount_range.max)} (step $${formatAmount(pp.loan_amount_range.increment)})` : '—'}</td>
          {/each}
        </tr>
        <tr>
          <td><strong>Loan purposes</strong></td>
          {#each perProduct as pp (pp?.jurisdiction + '/' + pp?.product)}
            <td>
              {pp ? `${pp.loan_purposes.length} purpose${pp.loan_purposes.length === 1 ? '' : 's'}` : '—'}
              {#if pp && pp.loan_purposes.length > 0}
                <details>
                  <summary class="muted">Show list</summary>
                  <ul class="purpose-list">
                    {#each pp.loan_purposes as purpose (purpose.id)}
                      <li>
                        <code>{purpose.id}</code> {purpose.display_name}
                        {#if purpose.amount_range}
                          <span class="muted">· {formatAmountRange(purpose.amount_range)}</span>
                        {/if}
                      </li>
                    {/each}
                  </ul>
                </details>
              {/if}
            </td>
          {/each}
        </tr>
        <tr>
          <td><strong>Establishment-fee schedule</strong></td>
          {#each perProduct as pp (pp?.jurisdiction + '/' + pp?.product)}
            <td class="diff-payload">{pp ? formatEstFeeTiers(pp.establishment_fee_tiers) : '—'}</td>
          {/each}
        </tr>
        <tr>
          <td><strong>Dishonour fee</strong></td>
          {#each perProduct as pp (pp?.jurisdiction + '/' + pp?.product)}
            <td class="num">{pp && pp.dishonour_fee !== null ? '$' + formatAmount(pp.dishonour_fee) : '—'}</td>
          {/each}
        </tr>
        <tr>
          <td><strong>Overdue fee</strong></td>
          {#each perProduct as pp (pp?.jurisdiction + '/' + pp?.product)}
            <td class="num">{pp && pp.overdue_fee !== null ? '$' + formatAmount(pp.overdue_fee) : '—'}</td>
          {/each}
        </tr>
        <tr>
          <td><strong>Included in comparison rate</strong></td>
          {#each perProduct as pp (pp?.jurisdiction + '/' + pp?.product)}
            <td>
              {#if !pp}
                —
              {:else if !pp.comparison_rate_required}
                <span class="muted">n/a (not required)</span>
              {:else if pp.included_in_comparison_rate.length === 0}
                <span class="muted">none</span>
              {:else}
                {pp.included_in_comparison_rate.join(' · ')}
              {/if}
            </td>
          {/each}
        </tr>
      </tbody>
    </table>
  </div>
</section>

<style>
  table.cube-table--matrix {
    margin-top: var(--space-3);
  }
  table.cube-table--per-product {
    margin-top: var(--space-3);
  }
  .header-group {
    border-bottom: 1px solid var(--surface-line-soft);
  }
  details {
    margin-top: var(--space-1);
  }
  details summary {
    cursor: pointer;
    font-size: var(--type-xs, 0.75rem);
  }
  .purpose-list {
    margin: var(--space-2) 0 0 0;
    padding-left: var(--space-4);
    font-size: var(--type-xs, 0.75rem);
  }
  .purpose-list li code {
    font-family: var(--font-mono);
    font-size: 0.9em;
  }
  .diff-payload {
    font-family: var(--font-mono);
    font-size: var(--type-xs, 0.75rem);
  }
</style>
